import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Anchor, LogOut, RefreshCw, Plus, FileDown, Mail, TrendingUp, Banknote, Clock, FileText, Bell, FilePlus2, Trash2 } from "lucide-react";
import { API } from "@/data";

const STATUS = ["nouveau", "en_cours", "traite"];
const LABELS = { nouveau: "Nouveau", en_cours: "En cours", traite: "Traité" };
const INV_STATUS = ["emise", "payee", "annulee"];
const INV_LABELS = { emise: "Émise", payee: "Payée", annulee: "Annulée" };

export default function AdminDashboard() {
  const [tab, setTab] = useState("devis");
  const [quotes, setQuotes] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const [rates, setRates] = useState(null);
  const [invForm, setInvForm] = useState({ client: "", email: "", rate: "1.40", currency: "USD", description: "", booking_number: "", container_number: "", vessel: "", departure_date: "", items: [{ label: "Freight", amount: "" }] });
  const navigate = useNavigate();
  const token = localStorage.getItem("bm_token");
  const headers = { Authorization: `Bearer ${token}` };

  const sumByCurrency = (list, filter) => {
    const acc = {};
    (list || []).filter(filter).forEach((i) => {
      acc[i.currency] = (acc[i.currency] || 0) + (Number(i.amount) || 0);
    });
    const entries = Object.entries(acc);
    return entries.length ? entries.map(([c, v]) => `${v.toLocaleString("fr-CA")} ${c}`).join(" · ") : "—";
  };

  const guard = (e) => {
    if (e.response?.status === 401) {
      localStorage.removeItem("bm_token");
      navigate("/admin/login");
    }
  };

  const load = () => {
    axios.get(`${API}/admin/quotes`, { headers }).then((r) => setQuotes(r.data)).catch(guard);
    axios.get(`${API}/admin/invoices`, { headers }).then((r) => setInvoices(r.data)).catch(guard);
    axios.get(`${API}/admin/rates`, { headers }).then((r) => setRates(r.data)).catch(guard);
  };

  useEffect(() => {
    if (!token) navigate("/admin/login");
    else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/admin/quotes/${id}`, { status }, { headers }).catch(() => {});
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
  };

  const setInvStatus = async (id, status) => {
    await axios.patch(`${API}/admin/invoices/${id}`, { status }, { headers }).catch(() => {});
    setInvoices((inv) => inv.map((i) => (i.id === id ? { ...i, status } : i)));
  };

  const RUBRIQUES = ["Freight", "Detention", "Correction BL", "Inspection douane", "Autre"];

  const setItem = (idx, key, value) => {
    setInvForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)) }));
  };
  const addItem = () => setInvForm((f) => ({ ...f, items: [...f.items, { label: "Detention", amount: "" }] }));
  const removeItem = (idx) => setInvForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const totalUsd = invForm.items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);

  const createInvoice = async (e) => {
    e.preventDefault();
    const items = invForm.items
      .filter((it) => parseFloat(it.amount) > 0)
      .map((it) => ({ label: it.label, amount: parseFloat(it.amount) }));
    if (!items.length) {
      toast.error("Ajoutez au moins une rubrique avec un montant");
      return;
    }
    try {
      await axios.post(`${API}/admin/invoices`, { ...invForm, items, amount: totalUsd, rate: parseFloat(invForm.rate) || null }, { headers });
      setShowForm(false);
      setInvForm({ client: "", email: "", rate: "1.40", currency: "USD", description: "", booking_number: "", container_number: "", vessel: "", departure_date: "", items: [{ label: "Freight", amount: "" }] });
      load();
    } catch {}
  };

  const download = (data, filename) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportFile = async (fmt) => {
    const kind = tab === "devis" ? "quotes" : "invoices";
    try {
      const res = await axios.get(`${API}/admin/export/${kind}?format=${fmt}`, { headers, responseType: "blob" });
      download(res.data, `${tab}-sodimax.${fmt}`);
    } catch {}
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${inv.id}/pdf`, { headers, responseType: "blob" });
      download(res.data, `${inv.number}.pdf`);
    } catch {}
  };

  const sendInvoice = async (inv) => {
    try {
      await axios.post(`${API}/admin/invoices/${inv.id}/send`, {}, { headers });
      toast.success(`Facture ${inv.number} envoyée à ${inv.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    }
  };

  const remindInvoice = async (inv) => {
    try {
      await axios.post(`${API}/admin/invoices/${inv.id}/remind`, {}, { headers });
      toast.success(`Relance envoyée à ${inv.email} pour ${inv.number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la relance");
    }
  };

  const quoteToInvoice = async (q) => {
    const amount = window.prompt(`Montant de la facture pour ${q.name} (USD) :`, "");
    if (amount === null) return;
    try {
      const { data } = await axios.post(`${API}/admin/invoices/from-quote/${q.id}`, { amount: parseFloat(amount) || 0, currency: "USD" }, { headers });
      toast.success(`Facture ${data.number} créée pour ${q.name} — devis marqué traité`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la création");
    }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Supprimer le devis de ${q.name} ?`)) return;
    try {
      await axios.delete(`${API}/admin/quotes/${q.id}`, { headers });
      toast.success("Devis supprimé");
      setQuotes((qs) => qs.filter((x) => x.id !== q.id));
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Supprimer la facture ${inv.number} ?`)) return;
    try {
      await axios.delete(`${API}/admin/invoices/${inv.id}`, { headers });
      toast.success(`Facture ${inv.number} supprimée`);
      setInvoices((inv2) => inv2.filter((x) => x.id !== inv.id));
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const logout = () => {
    localStorage.removeItem("bm_token");
    navigate("/admin/login");
  };

  const visibleInvoices = (invoices || []).filter((i) => !unpaidOnly || i.status === "emise");

  return (
    <div className="min-h-screen bg-abyss" data-testid="admin-dashboard">
      <div className="noise-overlay" />
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 bg-ember flex items-center justify-center rounded-sm"><Anchor size={16} /></span>
            <span className="font-display font-semibold uppercase tracking-wide">Sodimax Admin</span>
          </div>
          <div className="flex items-center gap-4">
            {tab !== "tarifs" && (
            <div className="flex items-center gap-2" data-testid="export-buttons">
              {[["xlsx", "Excel"], ["pdf", "PDF"], ["docx", "Word"]].map(([fmt, label]) => (
                <button key={fmt} onClick={() => exportFile(fmt)} data-testid={`export-${fmt}-btn`}
                  className="text-xs font-semibold border border-white/10 hover:border-ember hover:text-ember text-steel-300 px-3 py-1.5 rounded-sm transition-colors duration-300">
                  {label}
                </button>
              ))}
            </div>
            )}
            <button onClick={load} data-testid="admin-refresh-btn" className="text-steel-400 hover:text-white transition-colors"><RefreshCw size={16} /></button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm text-steel-400 hover:text-ember transition-colors">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10" data-testid="admin-stats">
          {[
            { id: "stats-invoiced", icon: TrendingUp, label: "CA facturé", value: sumByCurrency(invoices, () => true) },
            { id: "stats-collected", icon: Banknote, label: "CA encaissé", value: sumByCurrency(invoices, (i) => i.status === "payee") },
            { id: "stats-outstanding", icon: Clock, label: "En attente de paiement", value: sumByCurrency(invoices, (i) => i.status === "emise") },
            { id: "stats-pending-quotes", icon: FileText, label: "Devis en attente", value: quotes ? String(quotes.filter((q) => q.status === "nouveau").length) : "—" },
          ].map((s) => (
            <div key={s.id} className="bg-abyss p-6" data-testid={s.id}>
              <s.icon size={18} className="text-ember" />
              <p className="mt-4 font-display text-xl lg:text-2xl font-semibold">{s.value}</p>
              <p className="text-[10px] uppercase tracking-widest text-steel-500 mt-2">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-10" data-testid="admin-tabs">
          {[["devis", "Demandes de devis"], ["factures", "Factures"], ["tarifs", "Grille tarifaire"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-widest border transition-all duration-300 rounded-sm ${
                tab === k ? "bg-ember border-ember text-white" : "border-white/10 text-steel-400 hover:border-white/30"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "devis" && (
          <>
            <div className="flex items-end justify-between mb-8">
              <h1 className="font-display text-3xl font-bold tracking-tight">Demandes de devis</h1>
              {quotes && (
                <p className="text-sm text-steel-400" data-testid="admin-quote-count">
                  Total : <span className="text-white font-semibold">{quotes.length}</span> · Nouveaux : <span className="text-ember font-semibold">{quotes.filter((q) => q.status === "nouveau").length}</span>
                </p>
              )}
            </div>
            {!quotes ? (
              <p className="text-steel-500">Chargement…</p>
            ) : quotes.length === 0 ? (
              <p className="text-steel-500" data-testid="admin-empty">Aucune demande pour le moment.</p>
            ) : (
              <div className="border border-white/10 bg-surface overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-quotes-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-steel-500 border-b border-white/10">
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4">Client</th>
                      <th className="px-5 py-4">Contact</th>
                      <th className="px-5 py-4">Route</th>
                      <th className="px-5 py-4">Conteneur</th>
                      <th className="px-5 py-4">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((q) => (
                      <tr key={q.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`admin-quote-row-${q.id}`}>
                        <td className="px-5 py-4 font-mono text-xs text-steel-400">{String(q.created_at).slice(0, 10)}</td>
                        <td className="px-5 py-4">
                          <p className="font-medium">{q.name}</p>
                          {q.company && <p className="text-xs text-steel-500">{q.company}</p>}
                        </td>
                        <td className="px-5 py-4 text-xs text-steel-400">
                          <p>{q.email}</p>
                          {q.phone && <p>{q.phone}</p>}
                        </td>
                        <td className="px-5 py-4 uppercase text-xs tracking-wide">{q.origin} → {q.destination}</td>
                        <td className="px-5 py-4 text-xs">{q.quantity} × {q.container_type}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} data-testid={`admin-status-select-${q.id}`}
                              className="bg-abyss border border-white/10 text-xs px-2 py-1.5 rounded-sm outline-none focus:border-ember">
                              {STATUS.map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
                            </select>
                            {q.status !== "traite" && (
                              <button onClick={() => quoteToInvoice(q)} data-testid={`quote-to-invoice-btn-${q.id}`}
                                className="text-steel-400 hover:text-ember transition-colors" title="Convertir en facture">
                                <FilePlus2 size={16} />
                              </button>
                            )}
                            <button onClick={() => deleteQuote(q)} data-testid={`quote-delete-btn-${q.id}`}
                              className="text-steel-400 hover:text-red-400 transition-colors" title="Supprimer le devis">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "factures" && (
          <>
            <div className="flex items-end justify-between mb-8">
              <h1 className="font-display text-3xl font-bold tracking-tight">Factures</h1>
              <div className="flex items-center gap-3">
                <button onClick={() => setUnpaidOnly(!unpaidOnly)} data-testid="unpaid-filter-btn"
                  className={`text-xs font-semibold px-4 py-2.5 rounded-sm border transition-all duration-300 ${
                    unpaidOnly ? "bg-ember border-ember text-white" : "border-white/10 text-steel-400 hover:border-white/30"}`}>
                  Impayées
                </button>
                <button onClick={() => setShowForm(!showForm)} data-testid="new-invoice-btn"
                  className="bg-ember hover:bg-emberdark text-white text-xs font-semibold px-5 py-2.5 rounded-sm transition-all duration-300 flex items-center gap-2">
                  <Plus size={14} /> Nouvelle facture
                </button>
              </div>
            </div>

            {showForm && (
              <form onSubmit={createInvoice} className="mb-8 bg-surface border border-white/10 p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="invoice-form">
                <input required placeholder="Client" value={invForm.client} onChange={(e) => setInvForm({ ...invForm, client: e.target.value })} className="input-tactical" data-testid="invoice-client-input" />
                <input placeholder="Courriel" type="email" value={invForm.email} onChange={(e) => setInvForm({ ...invForm, email: e.target.value })} className="input-tactical" data-testid="invoice-email-input" />
                <div className="sm:col-span-2 lg:col-span-3 space-y-3" data-testid="invoice-items">
                  {invForm.items.map((item, idx) => (
                    <div key={idx} className="flex gap-3 items-center">
                      <select value={item.label} onChange={(e) => setItem(idx, "label", e.target.value)} className="input-tactical w-56 shrink-0" data-testid={`invoice-item-label-${idx}`}>
                        {RUBRIQUES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <input required type="number" step="0.01" min="0" placeholder="Montant USD" value={item.amount}
                        onChange={(e) => setItem(idx, "amount", e.target.value)} className="input-tactical flex-1" data-testid={`invoice-item-amount-${idx}`} />
                      {invForm.items.length > 1 && (
                        <button type="button" onClick={() => removeItem(idx)} className="text-steel-500 hover:text-red-400 transition-colors" data-testid={`invoice-item-remove-${idx}`}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="flex flex-wrap items-center gap-4 pt-1">
                    <button type="button" onClick={addItem} data-testid="invoice-item-add"
                      className="text-xs text-ember hover:underline flex items-center gap-1">
                      <Plus size={12} /> Ajouter une rubrique
                    </button>
                    <div className="flex items-center gap-2 text-xs text-steel-400">
                      Taux : 1 USD =
                      <input type="number" step="0.0001" min="0" value={invForm.rate}
                        onChange={(e) => setInvForm({ ...invForm, rate: e.target.value })}
                        className="input-tactical !w-24 !py-1.5" data-testid="invoice-rate-input" />
                      CAD
                    </div>
                    <span className="ml-auto font-mono text-sm text-ember" data-testid="invoice-total">
                      Total : {totalUsd.toLocaleString("fr-CA")} USD ≈ {(totalUsd * (parseFloat(invForm.rate) || 0)).toLocaleString("fr-CA")} CAD
                    </span>
                  </div>
                </div>
                <select value={invForm.currency} onChange={(e) => setInvForm({ ...invForm, currency: e.target.value })} className="input-tactical" data-testid="invoice-currency-select">
                  <option value="USD">USD</option>
                  <option value="CAD">CAD</option>
                  <option value="XOF">XOF</option>
                </select>
                <input placeholder="Description (ex : Fret Montréal → Dakar)" value={invForm.description} onChange={(e) => setInvForm({ ...invForm, description: e.target.value })} className="input-tactical sm:col-span-2" data-testid="invoice-description-input" />
                <input placeholder="N° de booking" value={invForm.booking_number} onChange={(e) => setInvForm({ ...invForm, booking_number: e.target.value })} className="input-tactical font-mono" data-testid="invoice-booking-input" />
                <input placeholder="N° de conteneur" value={invForm.container_number} onChange={(e) => setInvForm({ ...invForm, container_number: e.target.value })} className="input-tactical font-mono" data-testid="invoice-container-input" />
                <input placeholder="Nom du navire" value={invForm.vessel} onChange={(e) => setInvForm({ ...invForm, vessel: e.target.value })} className="input-tactical" data-testid="invoice-vessel-input" />
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-steel-500 mb-2">Date de départ</label>
                  <input type="date" value={invForm.departure_date} onChange={(e) => setInvForm({ ...invForm, departure_date: e.target.value })} className="input-tactical" data-testid="invoice-departure-input" />
                </div>
                <button type="submit" data-testid="invoice-submit-btn"
                  className="bg-ember hover:bg-emberdark text-white text-xs font-semibold px-5 py-3 rounded-sm transition-all duration-300">
                  Créer la facture
                </button>
              </form>
            )}

            {!invoices ? (
              <p className="text-steel-500">Chargement…</p>
            ) : invoices.length === 0 ? (
              <p className="text-steel-500" data-testid="invoices-empty">Aucune facture pour le moment.</p>
            ) : (
              <div className="border border-white/10 bg-surface overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-invoices-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-steel-500 border-b border-white/10">
                      <th className="px-5 py-4">N°</th>
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4">Client</th>
                      <th className="px-5 py-4">Description</th>
                      <th className="px-5 py-4">Montant</th>
                      <th className="px-5 py-4">Statut</th>
                      <th className="px-5 py-4">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleInvoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`admin-invoice-row-${inv.id}`}>
                        <td className="px-5 py-4 font-mono text-xs text-ember font-semibold">{inv.number}</td>
                        <td className="px-5 py-4 font-mono text-xs text-steel-400">{String(inv.created_at).slice(0, 10)}</td>
                        <td className="px-5 py-4">
                          <p className="font-medium">{inv.client}</p>
                          {inv.email && <p className="text-xs text-steel-500">{inv.email}</p>}
                        </td>
                        <td className="px-5 py-4 text-xs text-steel-300">
                          {inv.description || "—"}
                          {(inv.booking_number || inv.container_number || inv.vessel || inv.departure_date) && (
                            <p className="text-[10px] text-steel-500 font-mono mt-1">
                              {[inv.booking_number && `Booking ${inv.booking_number}`, inv.container_number, inv.vessel, inv.departure_date && `Départ ${inv.departure_date}`].filter(Boolean).join(" · ")}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-4 font-mono text-xs">
                          {Number(inv.amount).toLocaleString("fr-CA")} {inv.currency}
                          {inv.rate && <p className="text-[10px] text-steel-500 mt-1">≈ {(inv.amount * inv.rate).toLocaleString("fr-CA")} CAD</p>}
                        </td>
                        <td className="px-5 py-4">
                          <select value={inv.status} onChange={(e) => setInvStatus(inv.id, e.target.value)} data-testid={`invoice-status-select-${inv.id}`}
                            className="bg-abyss border border-white/10 text-xs px-2 py-1.5 rounded-sm outline-none focus:border-ember">
                            {INV_STATUS.map((s) => <option key={s} value={s}>{INV_LABELS[s]}</option>)}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <button onClick={() => downloadInvoicePdf(inv)} data-testid={`invoice-pdf-btn-${inv.id}`}
                              className="text-steel-400 hover:text-ember transition-colors" title="Télécharger le PDF">
                              <FileDown size={16} />
                            </button>
                            <button onClick={() => sendInvoice(inv)} disabled={!inv.email} data-testid={`invoice-send-btn-${inv.id}`}
                              className="text-steel-400 hover:text-ember disabled:opacity-30 transition-colors" title={inv.email ? "Envoyer par e-mail" : "Aucun courriel client"}>
                              <Mail size={16} />
                            </button>
                            {inv.status === "emise" && (
                              <button onClick={() => remindInvoice(inv)} disabled={!inv.email} data-testid={`invoice-remind-btn-${inv.id}`}
                                className="text-steel-400 hover:text-ember disabled:opacity-30 transition-colors" title="Relancer par e-mail">
                                <Bell size={16} />
                              </button>
                            )}
                            <button onClick={() => deleteInvoice(inv)} data-testid={`invoice-delete-btn-${inv.id}`}
                              className="text-steel-400 hover:text-red-400 transition-colors" title="Supprimer la facture">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "tarifs" && (
          <>
            <div className="flex items-end justify-between mb-8">
              <h1 className="font-display text-3xl font-bold tracking-tight">Grille tarifaire</h1>
              <p className="text-xs uppercase tracking-widest text-steel-500" data-testid="rates-confidential">Confidentiel — usage interne</p>
            </div>
            {!rates ? (
              <p className="text-steel-500">Chargement…</p>
            ) : (
              <div className="border border-white/10 bg-surface overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]" data-testid="admin-rates-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-widest text-steel-500 border-b border-white/10">
                      <th className="px-6 py-5">Destination</th>
                      {["HAPAG", "MSC", "MAERSK", "CMA-CGM"].map((line) => <th key={line} className="px-6 py-5">{line}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {rates.map((r) => (
                      <tr key={r.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`rates-row-${r.id}`}>
                        <td className="px-6 py-5">
                          <p className="font-display font-semibold uppercase tracking-wide">{r.port}</p>
                          <p className="text-xs text-steel-500">{r.country}</p>
                        </td>
                        {["HAPAG", "MSC", "MAERSK", "CMA-CGM"].map((line) => (
                          <td key={line} className="px-6 py-5">
                            {r.pricing?.[line] ? (
                              <span className="font-mono text-ember font-semibold">{Number(r.pricing[line]).toLocaleString("fr-CA")} USD</span>
                            ) : (
                              <span className="text-[10px] uppercase tracking-widest text-steel-600">Sur devis</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
